import { useLocation } from "react-router-dom";
import DOMPurify from "dompurify";
import useCompanyProfile from "../hooks/useCompanyProfile";

const SITE_URL = "https://apprenticewala.in";

function JsonLd({ data, testId }) {
  const json = JSON.stringify(data)
    .replace(/</g, "\\u003c")
    .replace(/>/g, "\\u003e")
    .replace(/&/g, "\\u0026")
    .replace(/\u2028/g, "\\u2028")
    .replace(/\u2029/g, "\\u2029");
  const sanitizedJson = DOMPurify.sanitize(json, {
    ALLOWED_ATTR: [],
    ALLOWED_TAGS: [],
  });

  return (
    <script
      type="application/ld+json"
      data-testid={testId}
      dangerouslySetInnerHTML={{ __html: sanitizedJson }}
    />
  );
}

export function GlobalStructuredData() {
  const company = useCompanyProfile();
  const { pathname } = useLocation();
  const organization = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: company.name || "ApprenticeWala",
    url: SITE_URL,
    logo: company.header_logo_url || undefined,
    email: company.email || undefined,
    telephone: company.phone || undefined,
    address: company.address ? { "@type": "PostalAddress", streetAddress: company.address } : undefined,
  };
  const website = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "ApprenticeWala",
    url: SITE_URL,
    description: "Apprenticeship and entry-level job opportunities for skilled candidates in India.",
    inLanguage: ["en", "hi"],
  };
  const page = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "ApprenticeWala",
    url: `${SITE_URL}${pathname}`,
    isPartOf: { "@type": "WebSite", name: "ApprenticeWala", url: SITE_URL },
  };
  return <><JsonLd data={organization} testId="organization-structured-data" /><JsonLd data={website} testId="website-structured-data" /><JsonLd data={page} testId="page-structured-data" /></>;
}

export function JobStructuredData({ job }) {
  const data = {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    title: job.title,
    description: job.description,
    datePosted: job.posted_at,
    employmentType: "APPRENTICESHIP",
    hiringOrganization: { "@type": "Organization", name: job.company },
    jobLocation: { "@type": "Place", address: { "@type": "PostalAddress", addressLocality: job.location, addressCountry: "IN" } },
    occupationalCategory: job.trade,
    qualifications: job.education,
    url: `${SITE_URL}/jobs/${job.slug || job.id}`,
  };
  return <JsonLd data={data} testId="job-structured-data" />;
}

export function ArticleStructuredData({ post }) {
  const data = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    articleBody: post.content,
    image: post.cover_image_url,
    datePublished: post.published_date,
    dateModified: post.published_date,
    articleSection: post.category,
    mainEntityOfPage: `${SITE_URL}/blog/${post.id}`,
    publisher: { "@type": "Organization", name: "ApprenticeWala", url: SITE_URL },
  };
  return <JsonLd data={data} testId="article-structured-data" />;
}